import { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileText, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

// Use the worker bundled with pdfjs-dist (avoids CDN/download blockers)
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

/**
 * Renders the loan-car agreement PDF inline using react-pdf.
 * This avoids all browser PDF-blocking (popup blockers, iframe blockers,
 * SmartScreen, etc.) because the PDF is decoded in JS and drawn to a canvas
 * inside the React app.
 */
export default function AgreementPdfViewer({ open, onOpenChange, record, backendUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scale, setScale] = useState(1.1);
  const [pdfError, setPdfError] = useState(null);

  const pdfUrl = record?.loan_id
    ? `${backendUrl}/api/loan-cars/${record.loan_id}/agreement-pdf`
    : null;
  const downloadUrl = record?.loan_id
    ? `${backendUrl}/api/loan-cars/${record.loan_id}/agreement-pdf?download=1`
    : null;

  // Fetch the PDF as ArrayBuffer (so the cookie auth flows + we feed bytes
  // directly to pdf.js — no browser PDF handler involved at all).
  useEffect(() => {
    if (!open || !pdfUrl) return;
    let aborted = false;
    setLoading(true);
    setPdfError(null);
    setPdfData(null);
    setPageNumber(1);
    setNumPages(null);
    fetch(pdfUrl, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.arrayBuffer();
      })
      .then((buf) => {
        if (aborted) return;
        setPdfData(buf);
      })
      .catch((e) => {
        if (aborted) return;
        console.error('Failed to fetch agreement PDF:', e);
        setPdfError(e.message || 'Failed to load');
        toast.error('Failed to load signed agreement');
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => { aborted = true; };
  }, [open, pdfUrl]);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
    setNumPages(n);
  }, []);

  const goPrev = () => setPageNumber((p) => Math.max(1, p - 1));
  const goNext = () => setPageNumber((p) => Math.min(numPages || 1, p + 1));
  const zoomIn = () => setScale((s) => Math.min(2.5, +(s + 0.15).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.5, +(s - 0.15).toFixed(2)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] h-[92vh] flex flex-col p-0">
        <DialogHeader className="px-6 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Signed Loan Car Agreement
            {record && (
              <span className="text-sm font-normal text-slate-500 ml-2">
                — {record.customer_name} • {record.loan_car_rego}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Consolidated form details, terms &amp; conditions, signature, and engraved licence.
          </DialogDescription>
        </DialogHeader>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-2 border-b bg-slate-50">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={goPrev} disabled={!numPages || pageNumber <= 1} data-testid="pdf-prev">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-700">
              Page {pageNumber} / {numPages || '–'}
            </span>
            <Button size="sm" variant="outline" onClick={goNext} disabled={!numPages || pageNumber >= numPages} data-testid="pdf-next">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={zoomOut} disabled={scale <= 0.5}>
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-700 w-14 text-center">{Math.round(scale * 100)}%</span>
            <Button size="sm" variant="outline" onClick={zoomIn} disabled={scale >= 2.5}>
              <ZoomIn className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* PDF body */}
        <div className="flex-1 overflow-auto bg-slate-200 flex justify-center" data-testid="pdf-viewer-area">
          {loading || !pdfData ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-500 w-full">
              {pdfError ? (
                <p className="text-red-600">Failed to load: {pdfError}</p>
              ) : (
                <>
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent mb-3"></div>
                  <p>Loading signed agreement…</p>
                </>
              )}
            </div>
          ) : (
            <Document
              file={pdfData}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(e) => { setPdfError(e?.message || 'PDF parse error'); toast.error('Could not render PDF'); }}
              loading={(
                <div className="py-10 text-slate-500">Rendering…</div>
              )}
              className="my-4"
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                className="shadow-md"
              />
            </Document>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-white">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pdf-close-btn">
            Close
          </Button>
          {downloadUrl && (
            <Button asChild data-testid="pdf-download-btn">
              <a href={downloadUrl} rel="noopener">
                <FileText className="h-4 w-4 mr-2" />
                Download PDF
              </a>
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
